import { getRenderingOrder, getSelectedProjectId, getLastKnownBounds } from 'owa-timestream-store';
import { ASSUMED_ASSET_WIDTH, ASSET_PADDING } from 'owa-timestream-asset-sdk';
import type { AssetDescriptor } from 'owa-timestream-asset-sdk-types';
import { getOverlappingAssets } from '../viewstate';
import getCanvasDocumentBounds from './getCanvasDocumentBounds';
import { isCurrentCultureRightToLeft } from 'owa-localize';

interface Placement {
    coordinates: { x: number; y: number };
    overlapType: OverlapType;
    alignmentType: AlignmentType;
}

enum OverlapType {
    None = 0,
    MarginOuterFourth = 1,
    Margin = 2,
    Box = 3,
}

// in order of priority. "RightWithTop" means on the right hand side, aligned with the top, etc.
enum AlignmentType {
    RightWithTop = 0,
    BelowWithLeft = 1,
    LeftWithTop = 2,
    AboveWithLeft = 3,
    RightWithBottom = 4,
    BelowWithRight = 5,
    LeftWithBottom = 6,
    AboveWithRight = 7,
}

// in rtl, reverse left and right alignments
const rtlAlignmentMapping = [
    AlignmentType.LeftWithTop,
    AlignmentType.BelowWithRight,
    AlignmentType.RightWithTop,
    AlignmentType.AboveWithRight,
    AlignmentType.LeftWithBottom,
    AlignmentType.BelowWithLeft,
    AlignmentType.RightWithBottom,
    AlignmentType.AboveWithLeft,
];

export default function getNextAvailableAssetSpace(
    assetDescriptor: AssetDescriptor
): { x: number; y: number } | null {
    const { assetResizeConfiguration, createEmptyAsset } = assetDescriptor;

    const assetProps = createEmptyAsset();
    const resizeConfiguration = assetResizeConfiguration?.(assetProps.payload);
    const assetWidth = assetProps.width || resizeConfiguration?.defaultWidth || ASSUMED_ASSET_WIDTH;
    // reuse the default width parameter in the case that we don't know the height
    const assetHeight =
        assetProps.height || resizeConfiguration?.defaultHeight || ASSUMED_ASSET_WIDTH;

    const projectId = getSelectedProjectId();
    const assetIds = getRenderingOrder(projectId);

    // create boxes for existing assets
    let boxes: DOMRect[] = [];
    for (let i = 0; i < assetIds.length; i++) {
        const bounds = getLastKnownBounds(projectId, assetIds[i]);
        if (bounds) {
            boxes.push({
                ...bounds,
                left: bounds.x,
                right: bounds.x + bounds.width,
                top: bounds.y,
                bottom: bounds.y + bounds.height,
            } as DOMRect);
        }
    }

    const canvasDocumentBounds = getCanvasDocumentBounds();
    const canvasBounds = {
        top: canvasDocumentBounds.canvasTop,
        left: canvasDocumentBounds.canvasLeft,
        bottom: canvasDocumentBounds.canvasBottom,
        right: canvasDocumentBounds.canvasRight,
    } as DOMRect;

    boxes = boxes.filter(box => {
        return !isBoxFullyOutsideCurrentViewport(box, canvasBounds);
    }) as DOMRect[];

    const newBox: DOMRect = {
        left: 0,
        right: assetWidth,
        top: 0,
        bottom: assetHeight,
        width: assetWidth,
        height: assetHeight,
    } as DOMRect;

    const placements: Placement[] = [];
    for (let i = 0; i < boxes.length; i++) {
        const box = boxes[i];
        const alignments = createAlignments(box, assetWidth, assetHeight, ASSET_PADDING);
        for (let j = 0; j < alignments.length; j++) {
            const alignment = alignments[j];
            const coordinates = { x: alignment.x, y: alignment.y };
            const positionedBox = {
                ...newBox,
                ...coordinates,
                left: coordinates.x,
                right: newBox.width + coordinates.x,
                top: coordinates.y,
                bottom: newBox.height + coordinates.y,
            };
            let overlap = checkOverlapType(positionedBox, ASSET_PADDING);

            if (
                overlap !== OverlapType.Box &&
                !isBoxPartiallyOutsideCurrentViewport(positionedBox, canvasBounds)
            ) {
                const p = {
                    coordinates,
                    overlapType: overlap,
                    alignmentType: alignment.alignment,
                };
                placements.push(p);
            }
        }
    }

    const isRtl = isCurrentCultureRightToLeft();
    const sortedPlacements = placements.sort((p1, p2) => {
        return comparePlacements(
            p1,
            p2,
            canvasBounds.top,
            canvasBounds.left,
            canvasBounds.right,
            isRtl
        );
    });

    if (sortedPlacements.length === 0) {
        return null;
    }

    return sortedPlacements[0].coordinates;
}

/**
 * Tries to position the new box at position [newX, newY] and checks for overlaps with existing boxes.
 * @param box
 * @param marginSize
 */
function checkOverlapType(box: DOMRect, marginSize: number): OverlapType {
    const overlapsToTry = [
        {
            margin: 0,
            overlapType: OverlapType.Box,
        },
        {
            margin: marginSize * 0.75,
            overlapType: OverlapType.Margin,
        },
        {
            margin: marginSize,
            overlapType: OverlapType.MarginOuterFourth,
        },
    ];

    for (let i = 0; i < overlapsToTry.length; i++) {
        const overlap = overlapsToTry[i];
        const overlappingAssetIds = getOverlappingAssets(box, overlap.margin);
        if (overlappingAssetIds.length > 0) {
            return overlap.overlapType;
        }
    }
    return OverlapType.None;
}

/**
 * compare 2 placements. Returns -1 if p1 comes before p2, +1 if p1 comes after, and 0 if they're the same
 * @param p1
 * @param p2
 */
function comparePlacements(
    p1: Placement,
    p2: Placement,
    canvasTop: number,
    canvasLeft: number,
    canvasRight: number,
    isRtl: boolean
): number {
    if (
        p1.overlapType != p2.overlapType &&
        (p1.overlapType === OverlapType.Margin || p2.overlapType === OverlapType.Margin)
    ) {
        return p1.overlapType - p2.overlapType;
    }

    if (p1.alignmentType != p2.alignmentType) {
        return compareAlignments(p1.alignmentType, p2.alignmentType, isRtl);
    }

    const idealHorizontalEdge = isRtl ? canvasRight : canvasLeft;
    const p1Dist = Math.sqrt(
        Math.pow(p1.coordinates.x - idealHorizontalEdge, 2) +
            Math.pow(p1.coordinates.y - canvasTop, 2)
    );
    const p2Dist = Math.sqrt(
        Math.pow(p2.coordinates.x - idealHorizontalEdge, 2) +
            Math.pow(p2.coordinates.y - canvasTop, 2)
    );
    return p1Dist - p2Dist;
}

function compareAlignments(a1: AlignmentType, a2: AlignmentType, isRtl: boolean): number {
    if (!isRtl) {
        return a1 - a2;
    } else {
        return rtlAlignmentMapping[a1] - rtlAlignmentMapping[a2];
    }
}

/**
 * Creates the alignments we're going to try. Each alignment gives the (x, y) position of where to try positioning the new asset so that it
 * will be aligned with the existing asset
 * @param box the box we're aligning to
 * @param newAssetHeight
 * @param newAssetWidth
 */
function createAlignments(
    box: DOMRect,
    newAssetWidth: number,
    newAssetHeight: number,
    padding: number
) {
    return [
        {
            x: box.right + padding,
            y: box.top,
            alignment: AlignmentType.RightWithTop,
        },
        {
            x: box.left,
            y: box.bottom + padding,
            alignment: AlignmentType.BelowWithLeft,
        },
        {
            x: box.left - newAssetWidth - padding,
            y: box.top,
            alignment: AlignmentType.LeftWithTop,
        },
        {
            x: box.left,
            y: box.top - newAssetHeight - padding,
            alignment: AlignmentType.AboveWithLeft,
        },
        {
            x: box.right + padding,
            y: box.bottom - newAssetHeight,
            alignment: AlignmentType.RightWithBottom,
        },
        {
            x: box.right - newAssetWidth,
            y: box.bottom + padding,
            alignment: AlignmentType.BelowWithRight,
        },
        {
            x: box.left - newAssetWidth - padding,
            y: box.bottom - newAssetHeight,
            alignment: AlignmentType.LeftWithBottom,
        },
        {
            x: box.right - newAssetWidth,
            y: box.top - newAssetHeight - padding,
            alignment: AlignmentType.AboveWithRight,
        },
    ];
}

/**
 * Whether the given box sits completely ouside the viewport
 * @param box
 * @param canvasBounds
 */
function isBoxFullyOutsideCurrentViewport(box: DOMRect, canvasBounds: DOMRect): boolean {
    return (
        !box ||
        box.right < canvasBounds.left ||
        box.left > canvasBounds.right ||
        box.top > canvasBounds.bottom ||
        box.bottom < canvasBounds.top
    );
}

/**
 * Whether the given box overlaps the edge of the screen at all
 * @param box
 * @param canvasBounds
 */
function isBoxPartiallyOutsideCurrentViewport(box: DOMRect, canvasBounds: DOMRect): boolean {
    return (
        !box ||
        box.right > canvasBounds.right ||
        box.left < canvasBounds.left ||
        box.top < canvasBounds.top ||
        box.bottom > canvasBounds.bottom
    );
}
